import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import OrderTicket from '../../components/orders/OrderTicket';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('todos');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningOrder, setAssigningOrder] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingOrder, setRejectingOrder] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/orders/admin/all');
      setOrders(data.data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const { data } = await api.get('/drivers');
      setDrivers(data.data || []);
    } catch (error) {
      console.error('Error drivers:', error);
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchDrivers();
    const interval = setInterval(fetchOrders, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleConfirmPayment = async (id) => {
    if (!window.confirm('¿Confirmas que el pago es válido?')) return;
    try {
      await api.put(`/orders/${id}/confirm-payment`, {});
      fetchOrders();
      alert('✅ Pago confirmado');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const openRejectModal = (id) => {
    setRejectingOrder(id);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleRejectPayment = async () => {
    if (!rejectReason.trim()) {
      alert('Debes ingresar un motivo');
      return;
    }
    try {
      await api.put(`/orders/${rejectingOrder}/reject-payment`, { reason: rejectReason });
      setShowRejectModal(false);
      setRejectingOrder(null);
      setRejectReason('');
      fetchOrders();
      alert('❌ Pago rechazado');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleAssignDriver = async (driverId) => {
    try {
      await api.put(`/orders/${assigningOrder.id}/assign-driver`, { driverId });
      setShowAssignModal(false);
      setAssigningOrder(null);
      fetchOrders();
      fetchDrivers();
      alert('✅ Motorizado asignado');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.put(`/orders/${id}/status`, { status });
      fetchOrders();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const filters = [
    { value: 'todos', label: 'Todos', icon: '📋' },
    { value: 'por_verificar', label: 'Por verificar', icon: '💳' },
    { value: 'pendiente', label: 'Pendientes', icon: '⏳' },
    { value: 'aceptado', label: 'Aceptados', icon: '✅' },
    { value: 'preparando', label: 'Preparando', icon: '👨‍🍳' },
    { value: 'en_camino', label: 'En camino', icon: '🚗' },
    { value: 'entregado', label: 'Entregados', icon: '🎉' },
    { value: 'cancelado', label: 'Cancelados', icon: '❌' }
  ];

  let filtered = orders;
  if (filter === 'por_verificar') {
    filtered = orders.filter(o => o.payment_status === 'pagado');
  } else if (filter !== 'todos') {
    filtered = orders.filter(o => o.status === filter);
  }

  const counts = {
    todos: orders.length,
    por_verificar: orders.filter(o => o.payment_status === 'pagado').length,
    pendiente: orders.filter(o => o.status === 'pendiente').length,
    aceptado: orders.filter(o => o.status === 'aceptado').length,
    preparando: orders.filter(o => o.status === 'preparando').length,
    en_camino: orders.filter(o => o.status === 'en_camino').length,
    entregado: orders.filter(o => o.status === 'entregado').length,
    cancelado: orders.filter(o => o.status === 'cancelado').length
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl md:text-3xl font-serif font-bold">📋 Pedidos</h1>
        <button
          onClick={fetchOrders}
          className="bg-gold text-white px-4 py-2 rounded-full text-sm hover:bg-gold-dark transition"
        >
          🔄
        </button>
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {filters.map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition flex items-center gap-2 ${
              filter === f.value
                ? 'bg-primary text-white'
                : 'bg-white text-gray-600 border border-gray-200'
            }`}
          >
            <span>{f.icon}</span>
            <span className="hidden sm:inline">{f.label}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              filter === f.value ? 'bg-white/20' : 'bg-gray-100'
            }`}>
              {counts[f.value]}
            </span>
          </button>
        ))}
      </div>

      {counts.por_verificar > 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6 rounded-r-xl">
          <p className="font-semibold text-yellow-800">
            ⚠️ Tienes {counts.por_verificar} pago(s) por verificar
          </p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center text-gray-500">
          <div className="text-6xl mb-4">📭</div>
          <p>No hay pedidos</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(order => (
            <OrderTicket
              key={order.id}
              order={order}
              isAdmin={true}
              onStatusChange={handleStatusChange}
              onConfirmPayment={handleConfirmPayment}
              onRejectPayment={openRejectModal}
              onAssignDriver={(o) => {
                setAssigningOrder(o);
                setShowAssignModal(true);
              }}
            />
          ))}
        </div>
      )}

      {/* ===== MODAL ASIGNAR MOTORIZADO ===== */}
      {showAssignModal && assigningOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAssignModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-5 border-b flex justify-between items-center">
              <h2 className="text-lg font-bold">🚗 Asignar Motorizado</h2>
              <button onClick={() => setShowAssignModal(false)} className="text-2xl">✕</button>
            </div>
            <div className="p-5 space-y-2">
              <p className="text-sm text-gray-500 mb-3">
                Pedido #{assigningOrder.order_number}
              </p>
              {drivers.filter(d => d.is_available).length === 0 ? (
                <p className="text-center text-gray-500 py-4">
                  No hay motorizados disponibles
                </p>
              ) : (
                drivers.filter(d => d.is_available).map(driver => (
                  <button
                    key={driver.id}
                    onClick={() => handleAssignDriver(driver.id)}
                    className="w-full flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gold/10 transition text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-bold">
                      {driver.name?.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">{driver.name}</div>
                      <div className="text-xs text-gray-500">{driver.phone}</div>
                    </div>
                    <span className="text-green-500">✅</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL RECHAZAR PAGO ===== */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowRejectModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-5 border-b flex justify-between items-center">
              <h2 className="text-lg font-bold">❌ Rechazar Pago</h2>
              <button onClick={() => setShowRejectModal(false)} className="text-2xl">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Motivo del rechazo *
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500"
                  placeholder="Ej: El comprobante no es legible, referencia incorrecta, etc."
                  autoFocus
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowRejectModal(false)}
                  className="flex-1 py-3 border-2 border-gray-200 rounded-full font-semibold hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleRejectPayment}
                  disabled={!rejectReason.trim()}
                  className="flex-1 py-3 bg-red-500 text-white rounded-full font-semibold hover:bg-red-600 disabled:opacity-50"
                >
                  Rechazar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
